import React from 'react';
import { Pressable, View } from 'react-native';

import { formatRosterDate } from '../../../common/utils/people/roster';
import Text from '../../components/text/Text';
import { SwitchWithLabel } from '../../components';
import { Card } from '../../design/components';
import { useThemedStyles } from '../../hooks/useThemedStyles';
import DateSelect from './components/DateSelect';
import OptionSelect from './components/OptionSelect';
import { createMyLeaveRequestViewStyles } from './MyLeaveRequestView.styles';
import { pluralize, ROSTER_DATE_FORMAT } from './rosterHelpers';

const WEEKDAY_OPTIONS = [
	{ value: 1, label: 'Mon' },
	{ value: 2, label: 'Tue' },
	{ value: 3, label: 'Wed' },
	{ value: 4, label: 'Thu' },
	{ value: 5, label: 'Fri' },
	{ value: 6, label: 'Sat' },
	{ value: 0, label: 'Sun' },
];
const REPEAT_INTERVAL_OPTIONS = [
	{ value: 1, label: 'Weekly' },
	{ value: 2, label: 'Every 2 weeks' },
];

/**
 * RepeatUnavailabilityFields: the Repeat switch of a new unavailability entry
 * and, once on, its end date, frequency, weekdays and how many entries that makes.
 * Props: repeatEnabled, setRepeatEnabled, repeatUntilDate, setRepeatUntilDate,
 * repeatIntervalWeeks, setRepeatIntervalWeeks, repeatWeekdays,
 * toggleRepeatWeekday(weekday), repeatPreviewDates, minimumDate
 */
const RepeatUnavailabilityFields = ({
	repeatEnabled,
	setRepeatEnabled,
	repeatUntilDate,
	setRepeatUntilDate,
	repeatIntervalWeeks,
	setRepeatIntervalWeeks,
	repeatWeekdays,
	toggleRepeatWeekday,
	repeatPreviewDates,
	minimumDate,
}) => {
	const styles = useThemedStyles(createMyLeaveRequestViewStyles);

	return (
		<Card padded={false} style={styles.switchCard}>
			<SwitchWithLabel
				label="Repeat"
				value={repeatEnabled}
				onValueChange={setRepeatEnabled}
			/>
			{repeatEnabled ? (
				<View style={styles.repeatFields}>
					<DateSelect
						label="Repeat until"
						value={repeatUntilDate}
						onChange={setRepeatUntilDate}
						minimumDate={minimumDate}
					/>
					<OptionSelect
						label="Frequency"
						options={REPEAT_INTERVAL_OPTIONS}
						value={repeatIntervalWeeks}
						onChange={setRepeatIntervalWeeks}
					/>
					<View style={styles.weekdays}>
						{WEEKDAY_OPTIONS.map((option) => {
							const isSelected = repeatWeekdays.includes(option.value);

							return (
								<Pressable
									key={option.value}
									onPress={() => toggleRepeatWeekday(option.value)}
									accessibilityRole="checkbox"
									accessibilityLabel={option.label}
									accessibilityState={{ checked: isSelected }}
									style={({ pressed }) => [
										styles.weekday,
										isSelected && styles.weekdaySelected,
										pressed && styles.pressed,
									]}
								>
									<Text style={[styles.weekdayLabel, isSelected && styles.weekdayLabelSelected]}>
										{option.label}
									</Text>
								</Pressable>
							);
						})}
					</View>
					<Text style={styles.helperText}>
						{repeatPreviewDates.length
							? `${pluralize(repeatPreviewDates.length, 'entry', 'entries')} through ${formatRosterDate(repeatPreviewDates[repeatPreviewDates.length - 1], ROSTER_DATE_FORMAT)}`
							: 'Select at least one repeat day.'}
					</Text>
				</View>
			) : null}
		</Card>
	);
};

export default RepeatUnavailabilityFields;
